/* NUEVA VISTA DE GESTIÓN DE PROPIEDADES - MAQUETACIÓN TAILWIND */
import { useEffect, useState } from "react";
import {
    Search,
    MapPin,
    Calendar,
    ChevronLeft,
    ChevronRight,
    AlertTriangle,
    Loader2,
    Eye,
    History,
    Star,
    TrendingDown,
    X,
    Shield,
    FileText,
    Home,
    //Heart,
    //Share2,
    ArrowLeft
} from "lucide-react";
import { apiClient } from "../../lib/api";

// --- INTERFACES ---
export interface Property {
    id: number;
    title: string;
    location: string;
    date: string;
    authorName: string;
    authorAvatarUrl: string;
    imageUrl: string;
    description?: string;
    listingStatusId: number;
    statusLabel: string;
}
// Ruta de las imágenes hasta /Listings/
const BASE_URL_FOTOS = "https://devkq.eastus.cloudapp.azure.com:9091/Resources/Listings/";

const PropiedadesPage = () => {
    const [activeTab, setActiveTab] = useState("added");
    const [searchTerm, setSearchTerm] = useState("");
    const [propiedades, setPropiedades] = useState<Property[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<any>(null);

    // --- ESTADOS PARA EL TAB DINÁMICO ---
    const [showDetailTab, setShowDetailTab] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState<any>(null);

    // Estados para la carga del detalle
    const [detailData, setDetailData] = useState<any>(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [detailError, setDetailError] = useState<string | null>(null);

    // --- ESTADOS DEL MODAL ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // ESTADO PARA EL MODAL DE SERVICIOS
    const [isAmenitiesModalOpen, setIsAmenitiesModalOpen] = useState(false);

    // ESTADOS PARA IMÁGENES
    const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);
    const [activePhoto, setActivePhoto] = useState<string | null>(null); // <-- AÑADIR ESTE

    // Tabs con endpoints
    const tabs = [
        { id: "added", label: "Recién publicadas", icon: <History size={18} />, endpoint: 'ListingRent/RecentlyPublished' },
        { id: "rented", label: "Más rentadas", icon: <Star size={18} />, endpoint: 'ListingRent/MostRented' },
        { id: "support", label: "Menos rentadas", icon: <TrendingDown size={18} />, endpoint: 'ListingRent/LeastRented' },
    ];

    // ----------------------------------------------------------------------
    // 1. CARGA DE DATOS
    // ----------------------------------------------------------------------
    const fetchProperties = async () => {
        // No carga nada si estamos en el tab de detalle
        if (activeTab === "detail") return;

        setLoading(true);
        try {
            const currentEndpoint = tabs.find(t => t.id === activeTab)?.endpoint;
            const endpoint = `${currentEndpoint}?pageNumber=${currentPage}&pageSize=9&CityName=${encodeURIComponent(searchTerm)}`;
            const response = await apiClient.get<any>(endpoint);
            // const response = (await apiClient.get(endpoint)) as any;

            const mappedData = (response.data || []).map((item: any) => ({
                id: item.listingRentId,
                title: item.name,
                location: item.property?.cityName || 'Ubicación no disponible',
                date: item.listingRentConfirmationDate ? new Date(item.listingRentConfirmationDate).toLocaleDateString() : '---',
                authorName: `${item.owner.name} ${item.owner.lastName}`,
                authorAvatarUrl: item.owner.photoLink || '',
                imageUrl: item.photos.find((p: any) => p.isPrincipal)?.photoLink || item.photos[0]?.photoLink || '',
                listingStatusId: item.listingStatusId,
                statusLabel: item.listingStatusId === 6 ? 'Bloqueado' : 'Activo'
            }));

            setPropiedades(mappedData);
            setPagination(response.pagination);
        } catch (error) {
            setPropiedades([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProperties();
    }, [activeTab, currentPage, searchTerm]);

    // ----------------------------------------------------------------------
    // LÓGICA DE DETALLE
    // ----------------------------------------------------------------------
    const handleOpenDetailTab = async (prop: Property) => {
        setSelectedDetail(prop);
        setShowDetailTab(true);
        setActiveTab("detail");

        // Llamar a la API para cargar la información
        setLoadingDetail(true);
        setDetailError(null);
        try {
            const response = (await apiClient.get(`ListingRent/${prop.id}`)) as any;
            const data = response?.data || null;
            setDetailData(data);

            // Establece la foto principal por defecto
            if (data?.photos) {
                const principal = data.photos.find((p: any) => p.isPrincipal)?.photoLink || data.photos[0]?.photoLink;
                setActivePhoto(principal);
            }
        } catch (err) {
            console.error("Error al cargar detalle:", err);
            setDetailError("Ocurrió un error al cargar los detalles de la propiedad.");
        } finally {
            setLoadingDetail(false);
        }
    };

    const handleCloseDetail = () => {
        setActiveTab("added");
        setShowDetailTab(false);
        setDetailData(null); // Limpiar memoria
    };

    // ----------------------------------------------------------------------
    // LÓGICA DE BLOQUEO / DESBLOQUEO
    // ----------------------------------------------------------------------

    /* // Función para abrir el detalle en un nuevo Tab
    const handleOpenDetailTab = (prop: any) => {
        setSelectedDetail(prop);
        setShowDetailTab(true);
        setActiveTab("detail"); // Tab de detalle
    };

    // --- INYECCIÓN 2: FUNCIÓN PARA CERRAR EL DETALLE ---
    const handleCloseDetail = () => {
        setActiveTab("added"); // Vuelve a la pestaña de publicadas
        setShowDetailTab(false); // Opcional: oculta la pestaña de detalle
    }; */

    const togglePropertyStatus = async () => {
        if (!selectedProperty || isUpdating) return;
        setIsUpdating(true);

        // Si el ID es 6, está bloqueado -> Llamamos a Unblocked
        // Si no es 6, está activo -> Llamamos a Blocked
        const isBlocked = selectedProperty.listingStatusId === 6;
        const endpoint = isBlocked ? "ListingRent/Unblocked" : "ListingRent/Blocked";

        try {
            // Se envía como query param: ?listingRentId=XXX
            await apiClient.put(`${endpoint}?listingRentId=${selectedProperty.id}`, {});

            setIsModalOpen(false);
            fetchProperties(); // Recargar lista
        } catch (error) {
            console.error(error);
            alert("Error al cambiar el estado de la propiedad.");
        } finally {
            setIsUpdating(false);
        }
    };

    // ----------------------------------------------------------------------
    // LÓGICA PARA LAS IMÁGENES
    // ----------------------------------------------------------------------

    const handleNextImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!detailData?.photos) return;
        const currentIndex = detailData.photos.findIndex((p: any) => p.photoLink === fullscreenImage);
        const nextIndex = (currentIndex + 1) % detailData.photos.length;
        setFullscreenImage(detailData.photos[nextIndex].photoLink);
    };

    const handlePrevImage = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!detailData?.photos) return;
        const currentIndex = detailData.photos.findIndex((p: any) => p.photoLink === fullscreenImage);
        const prevIndex = (currentIndex - 1 + detailData.photos.length) % detailData.photos.length;
        setFullscreenImage(detailData.photos[prevIndex].photoLink);
    };

    return (
        <div className="space-y-6 relative">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold text-foreground">Gestión de Propiedades</h1>
                    <p className="text-muted-foreground mt-1">Administra el inventario de inmuebles y su estado</p>
                </div>
            </div>

            {/* TABS DE FILTROS */}
            <div className="border-b border-border">
                <div className="flex gap-8 overflow-x-auto no-scrollbar">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => { setActiveTab(tab.id); setCurrentPage(1); }}
                            className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-all ${activeTab === tab.id ? "border-primary text-primary" : "border-transparent text-muted-foreground"
                                }`}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    ))}

                    {/* TAB DE DETALLE (Solo aparece si se hace clic en Detalles) */}
                    {showDetailTab && selectedDetail && (
                        <button
                            onClick={() => setActiveTab("detail")}
                            className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-all ${activeTab === "detail" ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                        >
                            <Eye size={18} />
                            Detalle: {selectedDetail?.title.substring(0, 15)}...
                            <X
                                size={14}
                                className="ml-2 hover:text-white"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowDetailTab(false);
                                    setActiveTab("added");
                                }}
                            />
                        </button>
                    )}
                </div>
            </div>

            <div className="bg-card rounded-soft border border-border p-6 shadow-soft min-h-[500px]">
                {/* RENDERIZADO CONDICIONAL: LISTA O DETALLE */}
                {activeTab === "detail" ? (

                    <div className="animate-in fade-in slide-in-from-bottom-4 duration-300">

                        {loadingDetail ? (
                            <div className="flex flex-col items-center justify-center h-96">
                                <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
                                <p className="text-muted-foreground">Cargando detalles de la propiedad...</p>
                            </div>
                        ) : detailError || !detailData ? (
                            <div className="flex flex-col items-center justify-center h-96 text-center">
                                <p className="text-destructive mb-4">{detailError || "No se encontró información."}</p>
                                <button
                                    onClick={handleCloseDetail}
                                    className="px-4 py-2 bg-muted text-foreground rounded-soft hover:bg-muted/80 transition-colors"
                                >
                                    Volver a la lista
                                </button>
                            </div>
                        ) : (
                            /* --- VISTA DE DETALLE COMPLETA --- */
                            <>
                                {/* HEADER DETALLE */}
                                <div className="flex items-center justify-between mb-6">
                                    <button
                                        onClick={handleCloseDetail}
                                        className="flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-primary transition-colors"
                                    >
                                        <ArrowLeft size={18} /> Volver a la lista
                                    </button>
                                    {/* <div className="flex gap-2">
                                        <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-soft hover:bg-muted text-sm font-medium transition-colors">
                                            <Share2 size={16} /> Compartir
                                        </button>
                                        <button className="flex items-center gap-2 px-4 py-2 border border-border rounded-soft hover:bg-muted text-sm font-medium transition-colors">
                                            <Heart size={16} /> Guardar
                                        </button>
                                    </div> */}
                                </div>

                                {/* TÍTULO Y UBICACIÓN */}
                                <div className="mb-6">
                                    <h1 className="text-3xl font-heading font-bold text-foreground mb-2">{detailData.name}</h1>
                                    <div className="flex items-center gap-2 text-muted-foreground">
                                        <MapPin size={16} />
                                        <span className="underline cursor-pointer">
                                            {detailData.property?.address?.city?.city}, {detailData.property?.address?.city?.country}
                                        </span>
                                    </div>
                                </div>

                                {/* GALERÍA DE IMÁGENES INTERACTIVA */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-2 h-[400px] rounded-2xl overflow-hidden mb-8">
                                    {/* Imagen Principal (Grande) - Al hacer clic se abre completa */}
                                    <div
                                        className="md:col-span-2 h-full relative group bg-black cursor-zoom-in overflow-hidden"
                                        onClick={() => setFullscreenImage(activePhoto)}
                                    >
                                        <img
                                            src={`${activePhoto}`}
                                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 group-hover:opacity-90"
                                            alt="Principal"
                                            onError={(e) => (e.currentTarget.src = 'https://placehold.co/800x600?text=Sin+Imagen')}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                            <div className="bg-white/20 backdrop-blur-md p-2 rounded-full text-white border border-white/30">
                                                <Eye size={20} />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Miniaturas (Derecha) - CORREGIDO PARA MOSTRAR TODAS */}
                                    <div className="md:col-span-2 grid grid-cols-2 gap-2 h-full overflow-y-auto pr-1 no-scrollbar">
                                        {/* Usamos el array completo de photos en lugar de .slice(0, 4) */}
                                        {detailData.photos?.map((photo: any, index: number) => {
                                            const isSelected = activePhoto === photo.photoLink;
                                            return (
                                                <div
                                                    key={index}
                                                    className={`relative h-32 md:h-48 group overflow-hidden bg-muted cursor-pointer border-2 transition-all rounded-xl ${isSelected ? 'border-primary' : 'border-transparent hover:border-primary/50'
                                                        }`}
                                                    onClick={() => setActivePhoto(photo.photoLink)}
                                                >
                                                    <img
                                                        src={`${photo.photoLink}`}
                                                        className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${isSelected ? 'opacity-100' : 'opacity-70 group-hover:opacity-100'
                                                            }`}
                                                        alt={`Gallery ${index}`}
                                                        onError={(e) => (e.currentTarget.src = 'https://placehold.co/400x300?text=Sin+Imagen')}
                                                    />
                                                </div>
                                            );
                                        })}

                                        {/* Relleno visual solo si hay menos de 2 fotos para mantener la estructura */}
                                        {detailData.photos?.length < 2 && Array.from({ length: 2 - detailData.photos.length }).map((_, i) => (
                                            <div key={`placeholder-${i}`} className="bg-muted/50 rounded-xl border border-dashed border-border flex items-center justify-center text-muted-foreground/20 h-48">
                                                <Home size={24} />
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* CONTENIDO PRINCIPAL */}
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
                                    <div className="lg:col-span-2 space-y-8">

                                        <div className="flex items-center justify-between border-b border-border pb-6">
                                            <div>
                                                <h2 className="text-xl font-bold text-foreground">
                                                    {detailData.accomodationType?.accommodationName || 'Alojamiento'} • Anfitrión: {detailData.owner?.name} {detailData.owner?.lastName}
                                                </h2>

                                            </div>
                                            <div className="h-14 w-14 rounded-full overflow-hidden border border-border bg-muted shrink-0">
                                                <img
                                                    src={detailData.owner?.photoLink || 'https://placehold.co/150x150?text=User'}
                                                    className="w-full h-full object-cover"
                                                    alt="Host"
                                                />
                                            </div>
                                        </div>

                                        <div className="border-b border-border pb-6">
                                            <h3 className="text-lg font-bold mb-3 text-foreground">Descripción</h3>
                                            <p className="text-muted-foreground mt-1">
                                                {detailData.maxGuests} huéspedes • {detailData.bedrooms} habitaciones • {detailData.beds} camas • {detailData.bathrooms} baños
                                            </p>
                                            <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                                                {detailData.description}
                                            </p>
                                        </div>

                                        <div className="border-b border-border pb-6">
                                            <h3 className="text-lg font-bold mb-4 text-foreground">Servicios</h3>
                                            <div className="grid grid-cols-3 gap-4">
                                                {detailData.amenities?.slice(0, 6).map((amenity: any, idx: number) => (
                                                    <div key={idx} className="flex items-center gap-3 text-muted-foreground">
                                                        <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
                                                        <span className="truncate">{amenity.valueString || amenity.type}</span>
                                                    </div>
                                                ))}
                                            </div>
                                            {detailData.amenities?.length > 6 && (
                                                <button
                                                    onClick={() => setIsAmenitiesModalOpen(true)}
                                                    className="mt-4 px-4 py-2 border border-border rounded-soft text-sm text-gray-200 font-semibold hover:bg-muted transition-colors"
                                                >
                                                    Mostrar los {detailData.amenities.length} servicios
                                                </button>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-2">
                                            <div className="p-5 border border-border rounded-2xl bg-muted/30">
                                                <div className="flex items-center gap-2 mb-3 text-foreground">
                                                    <FileText size={20} />
                                                    <h4 className="font-bold">Reglas de la casa</h4>
                                                </div>
                                                <ul className="space-y-2 text-sm text-muted-foreground">
                                                    {detailData.rentRules?.slice(0, 4).map((rule: any, idx: number) => (
                                                        <li key={idx} className="flex gap-2">• {rule.name}</li>
                                                    )) || <li>Sin reglas específicas</li>}
                                                </ul>
                                            </div>

                                            <div className="p-5 border border-border rounded-2xl bg-muted/30">
                                                <div className="flex items-center gap-2 mb-3 text-foreground">
                                                    <Shield size={20} />
                                                    <h4 className="font-bold">Seguridad</h4>
                                                </div>
                                                <ul className="space-y-2 text-sm text-muted-foreground">
                                                    {detailData.securityItems?.slice(0, 4).map((item: any, idx: number) => (
                                                        <li key={idx} className="flex gap-2">• {item.description}</li>
                                                    )) || <li>No especificado</li>}
                                                </ul>
                                            </div>
                                        </div>
                                    </div>

                                    {/* TARJETA FLOTANTE DERECHA */}
                                    <div className="lg:col-span-1">
                                        <div className="sticky top-6 border border-border shadow-soft rounded-2xl p-6 bg-card">
                                            <div className="flex justify-between items-end mb-4">
                                                <div>
                                                    <span className="text-2xl font-bold text-foreground">
                                                        ${detailData.price?.priceNightly || 0}
                                                    </span>
                                                    <span className="text-muted-foreground"> / noche</span>
                                                </div>
                                                <div className="flex items-center gap-1 text-sm font-bold text-foreground">
                                                    <Star size={14} className="fill-foreground text-foreground" />
                                                    <span>{detailData.valoration || 'Nuevo'}</span>
                                                    <span className="text-muted-foreground font-normal">
                                                        • {detailData.reviews?.length || 0} reseñas
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="border border-border rounded-soft mb-4 p-4 bg-muted/10">
                                                <div className="flex justify-between items-center text-sm mb-2">
                                                    <span className="text-muted-foreground">Estado Actual:</span>
                                                    <span className={`font-bold px-2 py-0.5 rounded text-[10px] uppercase tracking-wider ${detailData.listingStatusId === 6 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                                                        {detailData.status || (detailData.listingStatusId === 6 ? 'Bloqueado' : 'Activo')}
                                                    </span>
                                                </div>
                                                <div className="flex justify-between items-center text-sm">
                                                    <span className="text-muted-foreground">Moneda:</span>
                                                    <span className="font-bold text-foreground">{detailData.price?.currency || 'BOB'}</span>
                                                </div>
                                            </div>

                                            {/* <div className="flex flex-col gap-3">
                                                <button className="w-full py-3 bg-primary text-primary-foreground font-bold rounded-soft hover:bg-primary/90 transition-colors shadow-sm">
                                                    Ver Calendario
                                                </button>
                                                <button className="w-full py-3 border border-border text-foreground font-bold rounded-soft hover:bg-muted transition-colors">
                                                    Contactar Anfitrión
                                                </button>
                                            </div> */}

                                            {/* <div className="mt-4 text-center">
                                                <p className="text-xs text-muted-foreground font-mono">
                                                    ID Propiedad: {detailData.listingRentId}
                                                </p>
                                            </div> */}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>


                ) : (
                    <>
                        {/* BUSCADOR */}
                        <div className="relative max-w-md mb-8">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Buscar por ciudad..."
                                className="w-full rounded-soft border bg-background pl-10 pr-4 py-2 text-sm text-gray-200 outline-none focus:ring-2 focus:ring-primary/20"
                                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                            />
                        </div>

                        {/* GRID DE PROPIEDADES */}
                        {loading ? (
                            <div className="py-20 flex flex-col items-center justify-center"><Loader2 className="animate-spin text-primary mb-2" /><p>Cargando propiedades...</p></div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                {propiedades.map((prop) => {
                                    const isBlocked = prop.listingStatusId === 6;
                                    return (
                                        <div key={prop.id} className="group bg-background rounded-soft border border-border overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col">
                                            <div className="relative h-44">
                                                <img
                                                    src={`${BASE_URL_FOTOS}${prop.imageUrl}`}
                                                    className="h-full w-full object-cover"
                                                    alt="Prop"
                                                    onError={(e) => { (e.target as HTMLImageElement).src = 'https://placehold.co/600x400?text=Error+Carga'; }}
                                                />
                                                <div className="absolute top-2 right-2">
                                                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white uppercase ${isBlocked ? 'bg-red-500' : 'bg-green-500'}`}>
                                                        {prop.statusLabel}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="p-4 flex flex-col flex-1">
                                                <h3 className="font-bold text-base text-white line-clamp-1 mb-1 uppercase">{prop.title}</h3>
                                                <div className="flex items-center gap-2 mb-3 text-muted-foreground italic">
                                                    <span className="text-xs">Por: {prop.authorName}</span>
                                                </div>

                                                <div className="text-[11px] text-muted-foreground space-y-1 mb-4">
                                                    <div className="flex items-center gap-1"><MapPin size={12} /> {prop.location}</div>
                                                    <div className="flex items-center gap-1"><Calendar size={12} /> {prop.date}</div>
                                                </div>

                                                <div className="mt-auto pt-3 border-t flex gap-2">
                                                    <button
                                                        onClick={() => handleOpenDetailTab(prop)}
                                                        className="flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] text-white font-bold border rounded-soft hover:bg-muted"
                                                    >
                                                        <Eye size={14} /> DETALLES
                                                    </button>
                                                    <button
                                                        onClick={() => { setSelectedProperty(prop); setIsModalOpen(true); }}
                                                        className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-bold rounded-soft border uppercase ${isBlocked ? "text-green-600 border-green-200" : "text-red-500 border-red-200"}`}
                                                    >
                                                        {isBlocked ? "Desbloquear" : "Bloquear"}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* PAGINACIÓN DINÁMICA - Solo se muestra si NO es el tab de detalle */}
                        {activeTab !== "detail" && pagination && pagination.totalPageCount > 1 && (
                            <div className="flex flex-col items-center gap-4 border-t border-border pt-8">
                                <div className="flex items-center gap-1">
                                    <button
                                        disabled={!pagination.hasPrevious}
                                        onClick={() => setCurrentPage(prev => prev - 1)}
                                        className="p-2.5 rounded-full hover:bg-muted text-muted-foreground disabled:opacity-30"
                                    >
                                        <ChevronLeft size={22} />
                                    </button>

                                    <span className="px-4 text-sm font-medium">
                                        Página {pagination.currentPage} de {pagination.totalPageCount}
                                    </span>

                                    <button
                                        disabled={!pagination.hasNext}
                                        onClick={() => setCurrentPage(prev => prev + 1)}
                                        className="p-2.5 rounded-full hover:bg-muted text-muted-foreground disabled:opacity-30"
                                    >
                                        <ChevronRight size={22} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* MODAL DE SERVICIOS (AMENIDADES) */}
            {isAmenitiesModalOpen && detailData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-card w-full max-w-lg max-h-[80vh] flex flex-col rounded-soft border border-border shadow-2xl animate-in fade-in zoom-in duration-200">
                        {/* Header del Modal */}
                        <div className="flex items-center justify-between p-6 border-b border-border">
                            <h2 className="text-xl font-bold text-foreground">Todos los servicios</h2>
                            <button
                                onClick={() => setIsAmenitiesModalOpen(false)}
                                className="p-2 text-muted-foreground hover:bg-muted rounded-full transition-colors"
                            >
                                <X size={20} />
                            </button>
                        </div>

                        {/* Cuerpo del Modal (Scrollable) */}
                        <div className="p-6 overflow-y-auto flex-1 no-scrollbar">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4">
                                {detailData.amenities?.map((amenity: any, idx: number) => (
                                    <div key={idx} className="flex items-center gap-3 text-foreground">
                                        <div className="h-2 w-2 rounded-full bg-primary shrink-0" />
                                        <span className="text-sm font-medium">{amenity.valueString || amenity.type}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer del Modal */}
                        {/* <div className="p-6 border-t border-border bg-muted/10 flex justify-end">
                            <button
                                onClick={() => setIsAmenitiesModalOpen(false)}
                                className="px-6 py-2.5 rounded-soft bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors"
                            >
                                Cerrar
                            </button>
                        </div> */}
                    </div>
                </div>
            )}

            {/* MODAL DE CONFIRMACIÓN */}
            {isModalOpen && selectedProperty && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-card w-full max-w-md rounded-soft border border-border p-6 shadow-2xl animate-in fade-in zoom-in duration-200">
                        {/* CORTINA DE BLOQUEO */}
                        {isUpdating && (
                            <div className="absolute inset-0 bg-background/60 z-10 flex items-center justify-center backdrop-blur-[1px]">
                                <Loader2 className="animate-spin text-primary" />
                            </div>
                        )}

                        <div className="flex items-center gap-4 text-orange-500 mb-4">
                            <div className="bg-orange-100 p-3 rounded-full">
                                <AlertTriangle size={24} />
                            </div>
                            <h2 className="text-xl font-bold text-foreground">¿Confirmar acción?</h2>
                        </div>
                        <p className="text-muted-foreground mb-6">
                            Estás a punto de {selectedProperty.listingStatusId === 6 ? 'desbloquear' : 'bloquear'} la propiedad <strong>{selectedProperty?.title}</strong>.
                            {selectedProperty.listingStatusId === 6 ? ' Volverá a estar disponible en el catálogo.' : ' Ya no será visible para los clientes.'}
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setIsModalOpen(false)}
                                disabled={isUpdating}
                                className="flex-1 px-4 py-2 text-sm text-white border rounded-soft hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={togglePropertyStatus}
                                disabled={isUpdating}
                                className={`px-4 py-2 text-sm font-medium text-white rounded-soft 
                                    ${isUpdating ? 'opacity-70 cursor-not-allowed' : ''}
                                    ${selectedProperty.listingStatusId === 6 ? 'bg-green-600 hover:bg-green-700' : 'bg-red-500 hover:bg-red-600'
                                    }`}
                            >
                                {isUpdating ? (
                                    <>
                                        <Loader2 size={16} className="animate-spin" />
                                        Procesando...
                                    </>
                                ) : (
                                    `Confirmar ${selectedProperty.listingStatusId === 6 ? 'Desbloqueo' : 'Bloqueo'}`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL LIGHTBOX CON NAVEGACIÓN */}
            {fullscreenImage && (
                <div
                    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-md p-4 animate-in fade-in duration-200 select-none"
                    onClick={() => setFullscreenImage(null)}
                >
                    {/* Botón Cerrar */}
                    <button
                        className="absolute top-6 right-6 p-3 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-all z-[110]"
                        onClick={() => setFullscreenImage(null)}
                    >
                        <X size={32} />
                    </button>

                    {/* Botón Anterior */}
                    <button
                        className="absolute left-4 md:left-10 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all z-[110]"
                        onClick={handlePrevImage}
                    >
                        <ChevronLeft size={48} strokeWidth={1.5} />
                    </button>

                    {/* Imagen Central */}
                    <div className="relative w-full h-full max-w-5xl flex flex-col items-center justify-center">
                        <img
                            src={fullscreenImage}
                            alt="Fullscreen"
                            className="max-w-full max-h-[85vh] object-contain shadow-2xl animate-in zoom-in-95 duration-300"
                            onClick={(e) => e.stopPropagation()}
                        />

                        {/* Contador de fotos */}
                        <div className="mt-6 px-4 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-white/80 text-xs font-bold tracking-widest uppercase">
                            {detailData.photos.findIndex((p: any) => p.photoLink === fullscreenImage) + 1} / {detailData.photos.length}
                        </div>
                    </div>

                    {/* Botón Siguiente */}
                    <button
                        className="absolute right-4 md:right-10 p-4 text-white/50 hover:text-white hover:bg-white/10 rounded-full transition-all z-[110]"
                        onClick={handleNextImage}
                    >
                        <ChevronRight size={48} strokeWidth={1.5} />
                    </button>
                </div>
            )}

        </div>
    );
};

export default PropiedadesPage;